import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import { formatearFecha } from '@/lib/fecha'
import { Banner } from '@/ui/Banner'
import { Boton } from '@/ui/Boton'
import { EstadoError } from '@/ui/EstadoError'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { Esqueleto } from '@/ui/Esqueleto'
import { InsigniaEstado } from '@/ui/InsigniaEstado'
import { ENCF, Monto } from '@/ui/Numero'
import {
  PRESENTACION_CARGO,
  PRESENTACION_ETAPA,
  monedaPresentable,
  nombrePeriodo,
  type CargoCuenta,
  type DatosTransferencia,
  type MiSuscripcion,
} from '@/dominio/cuenta/tipos'
import { descargarComprobante, useMiSuscripcion } from '@/dominio/cuenta/useCuenta'
import { Dato, EncabezadoPantalla, Tarjeta } from './-pantalla'

/**
 * Mi suscripción (`docs/adr/0020`): el plan y los complementos, lo que se
 * cobrará el mes que viene, el historial de cargos con su comprobante, y cómo
 * pagar por transferencia.
 *
 * **Se abre también con la cuenta suspendida**: es por donde se sale. Los datos
 * bancarios vienen del servidor (`Cobros__Transferencia__*`), nunca de este
 * código, y si Agentical Group no los publicó se dice en vez de inventarlos.
 *
 * Los montos son texto decimal y se pintan con `Monto`, sin aritmética de coma
 * flotante (regla 7).
 */
export const Route = createFileRoute('/suscripcion')({
  component: PantallaSuscripcion,
})

function PantallaSuscripcion() {
  const consulta = useMiSuscripcion()
  const cuenta = consulta.data

  return (
    <div className="flex max-w-[1100px] flex-col gap-6">
      <EncabezadoPantalla
        titulo="Mi suscripción"
        descripcion="Su plan con Agentical Group, lo que se cobra cada mes, el historial de pagos con su comprobante fiscal y cómo pagar."
      />

      {consulta.isError ? (
        <EstadoError
          titulo="No se pudo cargar la suscripción."
          contexto="Reintente en un momento."
          onReintentar={() => void consulta.refetch()}
        />
      ) : cuenta === undefined ? (
        <Esqueleto className="h-64 w-full" />
      ) : (
        <Contenido cuenta={cuenta} />
      )}
    </div>
  )
}

function Contenido({ cuenta }: { cuenta: MiSuscripcion }) {
  const etapa = PRESENTACION_ETAPA[cuenta.estado.etapa]
  const saldo = cuenta.estado.saldo

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-2">
        <Tarjeta titulo="Estado de la cuenta">
          <dl>
            <Dato etiqueta="Etapa">
              <InsigniaEstado tono={etapa.tono}>{etapa.etiqueta}</InsigniaEstado>
            </Dato>
            <Dato etiqueta="Pendiente de pago">
              <Monto valor={saldo} moneda={monedaPresentable(cuenta.estado.moneda)} mostrarMoneda />
            </Dato>
            {cuenta.estado.venceEl ? (
              <Dato etiqueta={cuenta.estado.causa === 'prueba' ? 'La prueba termina' : 'Vence'}>
                <span className="numero">{formatearFecha(cuenta.estado.venceEl)}</span>
              </Dato>
            ) : null}
            {cuenta.estado.prorrogaHasta ? (
              <Dato etiqueta="Prórroga hasta">
                <span className="numero">{formatearFecha(cuenta.estado.prorrogaHasta)}</span>
              </Dato>
            ) : null}
          </dl>
          <p className="mt-3 text-cuerpo text-gris-700">{cuenta.estado.mensaje}</p>
          <p className="mt-2 text-etiqueta text-gris-600">
            Aviso {cuenta.parametros.diasAviso} días antes del vencimiento · gracia hasta el día{' '}
            {cuenta.parametros.diasGracia} de atraso · sin emitir comprobantes nuevos del día{' '}
            {cuenta.parametros.diasGracia + 1} al {cuenta.parametros.diaSuspension - 1} · suspendida desde el día{' '}
            {cuenta.parametros.diaSuspension}. Lo ya emitido se sigue transmitiendo a DGII en todas las etapas.
          </p>
        </Tarjeta>

        <Tarjeta titulo="Plan y complementos">
          {cuenta.plan === null && cuenta.conceptos.length === 0 ? (
            <p className="text-cuerpo text-gris-700">
              Todavía no tiene un plan contratado. Escríbale a Agentical Group para elegir uno.
            </p>
          ) : (
            <dl>
              {cuenta.plan ? (
                <Dato etiqueta="Plan desde">
                  <span className="numero">{formatearFecha(cuenta.plan.desde)}</span>
                </Dato>
              ) : null}
              {cuenta.conceptos.map((c) => (
                <Dato key={c.concepto} etiqueta={c.nombre}>
                  {c.activo ? (
                    <>
                      {c.cantidad != null ? <span className="numero mr-1">{c.cantidad} ×</span> : null}
                      <Monto valor={c.precioMensual} moneda={monedaPresentable(c.moneda)} mostrarMoneda />
                      <span className="ml-1 text-etiqueta text-gris-600">
                        {c.cantidad != null ? 'por sucursal al mes + ITBIS' : 'al mes + ITBIS'}
                      </span>
                    </>
                  ) : (
                    <span className="text-gris-600">sin cargo</span>
                  )}
                </Dato>
              ))}
            </dl>
          )}

          {cuenta.proximoCargo ? (
            <div className="mt-3 rounded-md border border-gris-300 bg-gris-50 p-3">
              <p className="text-etiqueta font-medium uppercase tracking-wide text-gris-600">
                Próximo cargo · {nombrePeriodo(cuenta.proximoCargo.periodo)} · estimado
              </p>
              <p className="mt-1 flex flex-wrap items-baseline gap-x-3 text-cuerpo text-gris-900">
                <Monto
                  valor={cuenta.proximoCargo.total}
                  moneda={monedaPresentable(cuenta.proximoCargo.moneda)}
                  mostrarMoneda
                  className="text-seccion"
                />
                <span className="text-etiqueta text-gris-600">
                  <Monto valor={cuenta.proximoCargo.monto} /> más ITBIS <Monto valor={cuenta.proximoCargo.itbis} /> ·
                  vence el <span className="numero">{formatearFecha(cuenta.proximoCargo.venceEl)}</span>
                </span>
              </p>
            </div>
          ) : null}
        </Tarjeta>
      </div>

      <Tarjeta titulo="Cómo pagar por transferencia">
        {cuenta.transferencia ? (
          <DatosDePago datos={cuenta.transferencia} />
        ) : (
          <Banner tono="ambar" titulo="Agentical Group todavía no ha publicado los datos de transferencia">
            Escríbanos y se los enviamos. Cuando estén publicados aparecerán aquí.
          </Banner>
        )}
      </Tarjeta>

      <Tarjeta titulo="Historial de cargos" relleno={false}>
        <Historial cargos={cuenta.cargos} />
      </Tarjeta>
    </>
  )
}

function DatosDePago({ datos }: { datos: DatosTransferencia }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <dl>
        <Dato etiqueta="Banco">{datos.banco}</Dato>
        {datos.tipoCuenta ? <Dato etiqueta="Tipo de cuenta">{datos.tipoCuenta}</Dato> : null}
        <Dato etiqueta="Número de cuenta">
          <span className="numero">{datos.numeroCuenta}</span>
        </Dato>
        <Dato etiqueta="Titular">{datos.titular}</Dato>
        {datos.rncTitular ? (
          <Dato etiqueta="RNC del titular">
            <span className="numero">{datos.rncTitular}</span>
          </Dato>
        ) : null}
      </dl>
      <div className="flex flex-col gap-2 text-cuerpo text-gris-700">
        <p>
          Ponga en el concepto de la transferencia el <strong>número del cargo</strong> (columna «Nº» del historial).
          Agentical Group registra el pago al recibirlo, y la cuenta se pone al día en ese momento.
        </p>
        {datos.correo ? (
          <p>
            Si quiere acelerarlo, mande el comprobante de la transferencia a <span className="mono">{datos.correo}</span>.
          </p>
        ) : null}
        {datos.instrucciones ? <p>{datos.instrucciones}</p> : null}
        <p className="text-etiqueta text-gris-600">
          Por cada pago, Agentical Group le emite su comprobante fiscal electrónico, que puede descargar desde el historial.
        </p>
      </div>
    </div>
  )
}

function Historial({ cargos }: { cargos: CargoCuenta[] }) {
  const [descargando, setDescargando] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const th = 'h-[var(--alto-fila)] px-2 text-left text-etiqueta font-semibold text-gris-600'

  if (cargos.length === 0) {
    return <EstadoVacio variante="primera-vez" mensaje="Todavía no hay cargos en su cuenta." />
  }

  async function descargar(pagoId: string) {
    setError(null)
    setDescargando(pagoId)
    try {
      await descargarComprobante(pagoId)
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo descargar el comprobante.')
    } finally {
      setDescargando(null)
    }
  }

  return (
    <div className="flex flex-col">
      {error !== null ? (
        <Banner tono="rojo" titulo="No se pudo descargar el comprobante" className="m-3">
          {error}
        </Banner>
      ) : null}
      <table className="w-full border-collapse text-cuerpo" data-densidad="normal">
        <thead className="bg-gris-50">
          <tr className="border-b border-gris-300">
            <th scope="col" className={`${th} w-[80px]`}>
              Nº
            </th>
            <th scope="col" className={th}>
              Período
            </th>
            <th scope="col" className={th}>
              Concepto
            </th>
            <th scope="col" className={`${th} w-[140px] text-right`}>
              Total
            </th>
            <th scope="col" className={`${th} w-[110px]`}>
              Vence
            </th>
            <th scope="col" className={`${th} w-[140px]`}>
              Estado
            </th>
            <th scope="col" className={`${th} w-[220px]`}>
              Comprobante
            </th>
          </tr>
        </thead>
        <tbody>
          {cargos.map((c) => {
              const estado = PRESENTACION_CARGO[c.estado]
              const comprobante = c.comprobante
              return (
                <tr key={c.id} className="h-[var(--alto-fila)] border-b border-gris-200 last:border-b-0">
                  <td className="numero px-2 text-gris-700">{c.numero}</td>
                  <td className="px-2 text-gris-900">{nombrePeriodo(c.periodo)}</td>
                  <td className="px-2 text-gris-700">{c.descripcion}</td>
                  <td className="px-2 text-right">
                    <Monto valor={c.total} moneda={monedaPresentable(c.moneda)} />
                  </td>
                  <td className="numero px-2 text-gris-700">{formatearFecha(c.venceEl)}</td>
                  <td className="px-2">
                    <InsigniaEstado tono={estado.tono}>{estado.etiqueta}</InsigniaEstado>
                  </td>
                  <td className="px-2">
                    {comprobante === null ? (
                      <span className="text-gris-400">—</span>
                    ) : comprobante.estado === 'emitido' && comprobante.eNCF ? (
                      <span className="flex items-center gap-2">
                        <ENCF valor={comprobante.eNCF} abreviado />
                        {comprobante.descargable ? (
                          <Boton
                            tamano="menor"
                            variante="discreto"
                            onClick={() => void descargar(comprobante.pagoId)}
                            disabled={descargando === comprobante.pagoId}
                            aria-label="Descargar el comprobante"
                          >
                            <Download aria-hidden="true" className="size-4" />
                          </Boton>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-etiqueta text-gris-600">en preparación</span>
                    )}
                  </td>
                </tr>
              )
            })}
        </tbody>
      </table>
    </div>
  )
}
